import React, { useState } from "react";
import {
  useGetAllCouponsQuery,
  useCreateCouponMutation,
  useDeactivateCouponMutation,
  useDeleteCouponMutation,
} from "../../features/api/couponApiSlice";
import { useToast } from "../toast/ToastContext";
import ConfirmModal from "../modal/ConfirmModal"; // Centralized case-sensitive Linux-safe path import
import FuelLoading from "../../pages/FuelLoading";
import {
  FaTicketAlt,
  FaPlus,
  FaTrash,
  FaBan,
  FaRandom,
  FaPercent,
  FaDollarSign,
  FaCalendarAlt,
  FaUsers,
} from "react-icons/fa";
import "./adminCoupons.scss";
import AdminHeader from "../layout/AdminHeader";

export default function AdminCoupons() {
  const { showToast } = useToast();

  // Local Form Inputs States
  const [rawCodeSuffix, setRawCodeSuffix] = useState(""); // Captures ONLY the tail string text input
  const [discountPercent, setDiscountPercent] = useState("");
  const [type, setType] = useState("percentage");
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState("");
  const [usageLimit, setUsageLimit] = useState("");

  // Modal Dynamic States Configuration Mapping
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    actionMode: null,
    targetId: null,
    targetTitle: "",
  });

  // Data Pipes Query Infrastructure Hook triggers
  const { data: responseData, isLoading, isError } = useGetAllCouponsQuery();
  const [createCoupon, { isLoading: isCreating }] = useCreateCouponMutation();
  const [deactivateCoupon, { isLoading: isDeactivating }] =
    useDeactivateCouponMutation();
  const [deleteCoupon, { isLoading: isDeleting }] = useDeleteCouponMutation();

  const couponsList = responseData?.coupons || [];
  const isMutating = isCreating || isDeactivating || isDeleting;

  // 🟢 FORMAT FORCE AUTO-GENERATOR: Generates random alpha-numeric tail code adhering to brand tokens standard
  const handleGenerateRandomCode = () => {
    const charset = "ABCDEFGHJKLMNOPQRSTUVWXYZ23456789"; // Omitted ambiguous characters like 0, O, 1, I
    let generatedSuffix = "";
    for (let i = 0; i < 8; i++) {
      generatedSuffix += charset.charAt(
        Math.floor(Math.random() * charset.length)
      );
    }
    setRawCodeSuffix(generatedSuffix);
  };

  const resetFormChassis = () => {
    setRawCodeSuffix("");
    setDiscountPercent("");
    setType("percentage");
    setStartDate(new Date().toISOString().split("T")[0]);
    setEndDate("");
    setUsageLimit("");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!rawCodeSuffix.trim() || !discountPercent || !endDate) {
      showToast("Please fill out all required promo parameter slots.", "error");
      return;
    }

    // 🟢 FORMAT FORCE INTERCEPTOR: Merges prefix standard token with string suffix variables
    const finalFormattedCode = `MMA-${rawCodeSuffix.trim().toUpperCase()}`;

    const payload = {
      code: finalFormattedCode,
      discountPercent: Number(discountPercent),
      type,
      startDate: startDate || undefined,
      endDate,
      usageLimit: usageLimit ? Number(usageLimit) : null,
    };

    try {
      const result = await createCoupon(payload).unwrap();

      const createdCode = result?.coupon?.code || finalFormattedCode;

      showToast(
        `🎟️ Discount code "${createdCode}" was created successfully!`,
        "success"
      );
      resetFormChassis();
    } catch (err) {
      const serverMessage =
        err.data?.message || "Failed to issue new coupon code rules.";
      showToast(serverMessage, "error");
    }
  };

  // Trigger interception for modal popups
  const handleTriggerActionModal = (mode, id, title) => {
    setModalConfig({
      isOpen: true,
      actionMode: mode,
      targetId: id,
      targetTitle: title,
    });
  };

  // Modal execution router logic
  const handleExecuteModalAction = async () => {
    const { actionMode, targetId, targetTitle } = modalConfig;
    setModalConfig({
      isOpen: false,
      actionMode: null,
      targetId: null,
      targetTitle: "",
    });

    try {
      if (actionMode === "deactivate") {
        await deactivateCoupon(targetId).unwrap();
        showToast(
          `Promotion "${targetTitle}" has been expired instantly.`,
          "success"
        );
      } else if (actionMode === "delete") {
        await deleteCoupon(targetId).unwrap();
        showToast(
          `Promotion index "${targetTitle}" removed from databases lists completely.`,
          "success"
        );
      }
    } catch (err) {
      showToast(`Operation pipeline adjustment tracking failure.`, "error");
    }
  };

  const checkIsExpired = (dateString) => {
    return new Date(dateString) < new Date();
  };

  if (isLoading)
    return (
      <div className="admin-coupons-fallback">
        <FuelLoading text="Opening promotions desks..." />
      </div>
    );
  if (isError)
    return (
      <div className="admin-coupons-fallback">
        ⚠️ Error loading promotional campaign records ledger sheets.
      </div>
    );

  return (
    <div className="admin-coupons-manager fuel-fade-in">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={
          modalConfig.actionMode === "delete"
            ? "Purge Promotion Coupon"
            : "Instantly Expire Promotion"
        }
        message={
          modalConfig.actionMode === "delete"
            ? `Are you certain you want to delete "${modalConfig.targetTitle}" from system databases? This action cannot be reversed.`
            : `Force expiration lock parameters onto "${modalConfig.targetTitle}" immediately? This will block clients from checking out with it.`
        }
        confirmText={
          modalConfig.actionMode === "delete"
            ? "Delete Permanently"
            : "Deactivate Code"
        }
        onConfirm={handleExecuteModalAction}
        onCancel={() =>
          setModalConfig({
            isOpen: false,
            actionMode: null,
            targetId: null,
            targetTitle: "",
          })
        }
      />

      {isMutating && (
        <FuelLoading text="Syncing marketing ledgers..." fullScreen />
      )}

      <AdminHeader
        title="Mini Miles Promotional Engine"
        description="Configure promotional campaign coupons, enforce specific branding formats, manage limits, and deactivate active code rows instantly."
        badgeText="Marketing Hub"
      />

      <div className="coupons-workspace-grid">
        {/* LEFT COLUMN: BRAND-LOCKED COMPOSITION PANEL */}
        <section className="composer-form-card">
          <h2>Create Branded Coupon</h2>
          <p>
            Deploy a fresh promotion sequence. Codes are formatted as{" "}
            <strong>MMA-XXXXX</strong> to lock brand consistency.
          </p>

          <form onSubmit={handleFormSubmit} className="coupon-core-form">
            <div className="field-group">
              <label>Promo Code Entry *</label>
              <div className="brand-locked-input-wrapper">
                <span className="brand-prefix-tag">MMA-</span>
                <input
                  type="text"
                  value={rawCodeSuffix}
                  onChange={(e) =>
                    setRawCodeSuffix(
                      e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "")
                    )
                  }
                  placeholder="SUMMER26, BACKROADS"
                  required
                />
                <button
                  type="button"
                  className="btn-random-generator"
                  onClick={handleGenerateRandomCode}
                  title="Generate Randomized Tracking Code Suffix"
                >
                  <FaRandom /> Auto Gen
                </button>
              </div>
            </div>

            <div className="form-input-row halves">
              <div className="field-group">
                <label>Discount Value *</label>
                <div className="numeric-input-icon-wrapper">
                  <span className="prefix-icon-tag">
                    {type === "percentage" ? <FaPercent /> : <FaDollarSign />}
                  </span>
                  <input
                    type="number"
                    min="1"
                    max={type === "percentage" ? "100" : "1000"}
                    value={discountPercent}
                    onChange={(e) => setDiscountPercent(e.target.value)}
                    placeholder={type === "percentage" ? "20" : "15.00"}
                    required
                  />
                </div>
              </div>
              <div className="field-group">
                <label>Discount Schema Type *</label>
                <select value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="percentage">Percentage Off (%)</option>
                  <option value="fixed_usd">Fixed USD Deduct ($)</option>
                </select>
              </div>
            </div>

            <div className="form-input-row halves">
              <div className="field-group">
                <label>Campaign Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="field-group">
                <label>Expiration Lock Date *</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="field-group">
              <label>Absolute Usage Limit Threshold (Optional)</label>
              <div className="numeric-input-icon-wrapper">
                <span className="prefix-icon-tag">
                  <FaUsers />
                </span>
                <input
                  type="number"
                  min="1"
                  value={usageLimit}
                  onChange={(e) => setUsageLimit(e.target.value)}
                  placeholder="e.g. 50 (Leave blank for infinite uses)"
                />
              </div>
            </div>

            <button
              type="submit"
              className="fuel-primary-btn launch-promo-btn"
              disabled={isMutating}
            >
              <FaPlus style={{ marginRight: "6px" }} /> Initialize Promotional
              Coupon
            </button>
          </form>
        </section>

        {/* RIGHT COLUMN: HIGH DENSITY DATA AUDIT LIST */}
        <section className="coupons-display-pane">
          <div className="panel-header-title">
            <h3>Active Campaigns Registry Logs ({couponsList.length})</h3>
          </div>

          <div className="admin-scrolling-coupons-wrapper">
            {couponsList.length === 0 ? (
              <p className="empty-slate-message">
                No active discount campaign coupons registered inside system
                memory blocks.
              </p>
            ) : (
              <div className="coupons-stack-layout">
                {couponsList.map((item) => {
                  const expired = checkIsExpired(item.endDate);
                  const maxedOut =
                    item.usageLimit && item.usageCount >= item.usageLimit;
                  const inactive = expired || maxedOut;

                  return (
                    <div
                      key={item._id}
                      className={`coupon-row-card ${
                        inactive ? "coupon-row-card--inactive" : ""
                      }`}
                    >
                      <div className="card-ticket-avatar">
                        <FaTicketAlt />
                      </div>

                      <div className="coupon-meta-block">
                        <div className="card-headline-line">
                          <h4>{item.code}</h4>
                          <span className={`value-badge ${item.type}`}>
                            {item.type === "percentage"
                              ? `${item.discountPercent}% OFF`
                              : `-$${item.discountPercent}`}
                          </span>
                        </div>

                        <div className="card-sub-attribution-row">
                          <span>
                            <FaCalendarAlt /> Live:{" "}
                            {new Date(item.startDate).toLocaleDateString()} –{" "}
                            {new Date(item.endDate).toLocaleDateString()}
                          </span>
                          <span className="usage-counter-tag">
                            🧮 Used: <strong>{item.usageCount}</strong> /{" "}
                            {item.usageLimit || "∞"}
                          </span>
                        </div>

                        {inactive && (
                          <span className="status-error-tag-stamp">
                            {expired
                              ? "🛑 EXPIRED PROMOTION"
                              : "⚠️ LIMIT REACHED MAXIMUM"}
                          </span>
                        )}
                      </div>

                      <div className="coupon-row-card__controls">
                        {!inactive && (
                          <button
                            type="button"
                            className="btn-row-action deactivate"
                            onClick={() =>
                              handleTriggerActionModal(
                                "deactivate",
                                item._id,
                                item.code
                              )
                            }
                            title="Force Expiration Instantly"
                            disabled={isMutating}
                          >
                            <FaBan />
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-row-action delete"
                          onClick={() =>
                            handleTriggerActionModal(
                              "delete",
                              item._id,
                              item.code
                            )
                          }
                          title="Purge Promotional Campaign"
                          disabled={isMutating}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
